import {distinctUntilChanged} from 'rxjs/operators';
import {Injectable} from '@angular/core';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {EditorRegistryService} from '../../diagram-render/mx-graph/editor/editor-registry.service';
import * as detect from '../../diagram-render/helpers/shapes-detectors.util';
import {MatDrawer} from '@angular/material/sidenav';
import {DiagramTabsInEditEnum} from '../shared/models/diagram-rsb-model';
import {MxCell} from 'src/app/editor-interface/shared/models/mx-cell.interface';

export interface SidebarData {
  type: string;
  cell?: any | null;
}

export type SidebarSide = 'left' | 'right';

export enum ActivityDocSubTab {
  NOTES_AND_LINKS = 'NOTES_AND_LINKS',
  METADATA = 'METADATA'
}

@Injectable()
export class SidebarService {
  savedTextSel;
  leftSidebar: MatDrawer;
  rightSidebar: MatDrawer;
  selectedTabIndex: {[key: string]: number} = {};
  selectedSubTabIndex: {[key: string]: ActivityDocSubTab} = {};
  diagramTabIndex: BehaviorSubject<number> = new BehaviorSubject<number>(
    DiagramTabsInEditEnum.DETAILS
  );
  leftSidebarVisible = true;
  private sidebarDataSubject: BehaviorSubject<SidebarData> = new BehaviorSubject<SidebarData>({
    type: 'diagram'
  });
  public sidebarData$: Observable<SidebarData> = this.sidebarDataSubject
    .asObservable()
    .pipe(distinctUntilChanged((p, n) => p.type === n.type && p.cell === n.cell));

  constructor(
    private modeDataSvc: ModeDataService,
    private registry: EditorRegistryService
  ) {}

  public setup(left: MatDrawer, right: MatDrawer) {
    this.leftSidebar = left;
    this.rightSidebar = right;

    if (this.modeDataSvc.isEditMode()) {
      this.leftSidebar?.open();
    }

    this.modeDataSvc
      .onEdit(() => {
        if (this.leftSidebarVisible) {
          this.leftSidebar?.open();
        }
      })
      .onView(() => this.leftSidebar?.close());
  }

  applyDefaultLeftSidebarShow(show?) {
    this.leftSidebarVisible = show;
  }

  public close(side: SidebarSide): void {
    switch (side) {
      case 'left':
        this.leftSidebar?.close?.();
        break;

      case 'right':
        this.rightSidebar?.close?.();
        break;
    }
  }

  public toggle(side: SidebarSide) {
    switch (side) {
      case 'left':
        this.applyDefaultLeftSidebarShow(!this.leftSidebar.opened);
        this.leftSidebar?.toggle?.();
        break;

      case 'right':
        this.rightSidebar?.toggle?.();
        break;
    }
  }

  public refresh(mxKey?: string | number, tab?: number, subTab?: ActivityDocSubTab): void {
    const graph = this.registry.getGraph();
    const cell: MxCell | MxCell[] = mxKey ? graph.model.getCell(mxKey) : graph.selectionModel.cells;
    const sidebarData = this.makeSidebarData({cells: Array.isArray(cell) ? cell : [cell]});

    if (typeof tab !== 'undefined') {
      this.selectedTabIndex[sidebarData.type] = tab;
    }

    this.selectedSubTabIndex[sidebarData.type] = subTab ?? ActivityDocSubTab.NOTES_AND_LINKS;

    this.sidebarDataSubject.next(sidebarData);
  }

  private makeSidebarData(selectionModel): SidebarData {
    const graph = this.registry.getGraph();
    const selectionLength = selectionModel.cells.length;
    let cell = selectionModel.cells[0];

    if (graph.isEditing() && graph.cellEditor) {
      cell = graph.cellEditor.getEditingCell();
    }

    if (selectionLength > 1 || !cell) {
      return {type: 'diagram'};
    } else if (graph.isEditing()) {
      return {type: 'text-formatting', cell: cell};
    } else if (detect.isActivity(cell)) {
      return {type: 'activity', cell: cell};
    } else if (detect.isText(cell)) {
      return {type: 'text', cell: cell};
    } else if (detect.isSticker(cell)) {
      return {type: 'sticker', cell: cell};
    } else if (detect.isFlowline(cell)) {
      return {type: 'flowline', cell: cell};
    } else if (detect.isImage(cell)) {
      return {type: 'image', cell: cell};
    } else if (detect.isConnector(cell)) {
      return {type: 'connector', cell: cell};
    } else if (detect.isTerminator(cell)) {
      return {type: 'terminator', cell: cell};
    } else {
      return {type: 'diagram'};
    }
  }
}
